import { useCallback, useState, type ReactNode } from 'react'
import { fontOptions } from 'constants/fonts'
import { ITheme } from 'store/theme/types'
import { useFormContext } from 'react-hook-form'
import { Stack, Typography, Grid, Divider } from '@mui/material'
import { PlusIcon } from 'lucide-react'
import { Button, Card } from '@repo/ui'
import { useColorMode } from 'hooks'
import { ColorPickerField, RadiusField, FontSelectorField } from 'components'
import { UploadFontModal } from 'components/feedback/modals/UploadFontModal'
import { useGlossaryTerm } from 'docs/GlossaryProvider'
import {
  useCompanyFonts,
  useDeleteCompanyFont,
  type CompanyFont,
} from 'hooks/api/useCompanyFonts'

import { TOKEN_IMPACT, type StudioTokenName } from './tokenImpact'
import { STUDIO_THEME_DEFAULTS } from '@repo/types'
import {
  TokenImpactPanel,
  TokenImpactToggle,
  type HighlightPartCount,
} from './TokenImpactPanel'

interface PreviewProps {
  company_theme: ITheme
  /** Needed to list/upload this brand's own fonts (VRG-348). */
  companyId?: string
  /**
   * VRG-549 — which control's impact panel is open, or null. At most one, because the
   * preview can only outline one token at a time; two open panels would each claim a
   * highlight and only the last would be true.
   */
  impactToken?: StudioTokenName | null
  onToggleImpact?: (token: StudioTokenName) => void
  /** Which preview pane is on screen. Decides which half of the panel is measured. */
  impactSurface?: 'certificate' | 'email'
  /** What the live preview matched for `impactToken`. Measured there, never asserted here. */
  impactParts?: HighlightPartCount[]
  /** Reached but not outlined, because it paints nothing visible right now. See the panel. */
  impactInvisible?: number
  /** Name of the flow / template currently rendered, for "… — on Certificate". */
  previewLabel?: string
}

/**
 * One labelled control: title row, glossary description, the field, and its impact panel.
 *
 * ## The description is READ, not written
 *
 * Every one of these used to carry a hand-typed sentence in this file, and two of them
 * were false by the time anyone looked: "Border color — used for borders, dividers, and
 * disabled buttons" described a control that (before `4638eb743`) affected nothing in the
 * app and never touched a disabled button, and "Primary color — used for buttons, links,
 * and headings" omitted that the token seeds an eleven-step scale read in ~100 places.
 * That is exactly the drift §44 exists to stop, so the sentence now comes from
 * `docs/glossary.json` via `useGlossaryTerm` and is shared with the tooltip, the docs and
 * search retrieval. **Do not put a string literal back here.**
 *
 * ## Why the toggle is on the TITLE ROW and the panel is under the field
 *
 * The question "what does this change?" is asked *about the control*, so its affordance
 * belongs beside the control's name — where it is visible before the operator has decided
 * anything. The answer is long, so it opens below the field rather than pushing the field
 * down the form.
 */
const ThemeControl = ({
  token,
  label,
  impactToken,
  onToggleImpact,
  impactSurface = 'certificate',
  impactParts,
  impactInvisible,
  previewLabel,
  children,
}: {
  token: StudioTokenName
  label: string
  impactToken?: StudioTokenName | null
  onToggleImpact?: (token: StudioTokenName) => void
  impactSurface?: 'certificate' | 'email'
  impactParts?: HighlightPartCount[]
  impactInvisible?: number
  previewLabel?: string
  children: ReactNode
}) => {
  const entry = useGlossaryTerm(TOKEN_IMPACT[token].glossaryId)
  const active = impactToken === token

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
        pb={2}
      >
        <Typography variant="body2" color="text.primary">
          {label}
        </Typography>
        {onToggleImpact && (
          <TokenImpactToggle token={token} active={active} onToggle={onToggleImpact} />
        )}
      </Stack>
      {entry?.short && (
        <Typography variant="body3" color="text.secondary" pb={4} sx={{ lineHeight: 1.5 }}>
          {entry.short}
        </Typography>
      )}
      {children}
      <TokenImpactPanel
        token={token}
        active={active}
        surface={impactSurface}
        parts={impactParts}
        invisible={impactInvisible}
        previewLabel={previewLabel}
      />
    </Stack>
  )
}

export const ThemeForm = ({
  company_theme,
  companyId,
  impactToken = null,
  onToggleImpact,
  impactSurface = 'certificate',
  impactParts,
  impactInvisible,
  previewLabel,
}: PreviewProps) => {
  const methods = useFormContext()
  const [uploadOpen, setUploadOpen] = useState(false)

  const { data: customFonts } = useCompanyFonts(companyId)
  const deleteFont = useDeleteCompanyFont()

  // Memoized: it is a dependency of the pickers' item list, so an unstable
  // identity here rebuilds that list on every render.
  const handleRemoveFont = useCallback((font: CompanyFont) => {
    if (!companyId) return
    deleteFont.mutate({ companyId, fontId: font.id })
    // If the removed font was in use, fall back to the theme's saved value
    // rather than leaving the field pointing at a font no longer offered.
    ;(['font', 'title_font'] as const).forEach((field) => {
      if (methods.getValues(field) === font.file_url) {
        methods.setValue(field, company_theme?.[field] ?? '')
      }
    })
  }, [companyId, deleteFont, methods, company_theme])
  /*
    VRG-720 — the swatch for an UNSET token must be what the APP renders, not a
    plausible-looking literal. `#ffffff` was shown while every page rendered
    `#F7F7F8`, which made the preview look like it was failing to inherit the
    background; typing `#ffffff` in "fixed" it only by storing a value that had
    never existed. Both values now come from `STUDIO_THEME_DEFAULTS`, which the
    app's palette is documented against.

    The dark branch is the EDITOR's colour mode, not the brand's, and the customer
    app has no dark palette to agree with — left as it was rather than changed blind.
  */
  const { resolvedMode } = useColorMode()
  const defaultBg = resolvedMode === 'dark' ? '#000000' : STUDIO_THEME_DEFAULTS.bg_color

  // Everything `ThemeControl` needs that is the same for all seven, gathered once so a
  // new control is one line plus its field rather than eight repeated props.
  const impact = {
    impactToken,
    onToggleImpact,
    impactSurface,
    impactParts,
    impactInvisible,
    previewLabel,
  }

  return (
    <Stack
    data-tour="studio-theme-form"
    spacing={4}
    sx={{ width: '100%' }}>
      {/* COLORS */}
      <Grid item md={12}>
        <Card title="Colors" spacing={6}>
          <ThemeControl token="primary_color" label="Primary color" {...impact}>
            <ColorPickerField
              {...methods}
              fieldName={'primary_color'}
              defaultValue={company_theme.primary_color || STUDIO_THEME_DEFAULTS.primary_color}
            />
          </ThemeControl>
          <Divider />
          <ThemeControl token="secondary_color" label="Border color" {...impact}>
            <ColorPickerField
              {...methods}
              fieldName={'secondary_color'}
              defaultValue={company_theme.secondary_color || STUDIO_THEME_DEFAULTS.secondary_color}
            />
          </ThemeControl>
          <Divider />
          <ThemeControl token="text_color" label="Text color" {...impact}>
            <ColorPickerField
              {...methods}
              fieldName={'text_color'}
              defaultValue={company_theme.text_color || STUDIO_THEME_DEFAULTS.text_color}
            />
          </ThemeControl>
          <Divider />
          <ThemeControl token="bg_color" label="Background color" {...impact}>
            <ColorPickerField
              {...methods}
              fieldName={'bg_color'}
              defaultValue={company_theme.bg_color || defaultBg}
            />
          </ThemeControl>
        </Card>
      </Grid>

      {/* FONTS */}
      <Grid item md={12}>
        <Card
          title="Fonts"
          spacing={6}
          // One upload control for the whole card, not one per picker: an
          // uploaded font is available to BOTH the title and body fields, so a
          // control on each would imply two separate libraries.
          action={
            <Button
              variant="secondary"
              startIcon={<PlusIcon size={16} strokeWidth={1} />}
              onClick={() => setUploadOpen(true)}
              disabled={!companyId}
            >
              Upload font
            </Button>
          }
        >
          <ThemeControl token="title_font" label="Title font" {...impact}>
            <FontSelectorField
              {...methods}
              defaultValue={company_theme?.title_font || company_theme?.font}
              fieldName={'title_font'}
              options={fontOptions}
              customFonts={customFonts}
              onRemoveCustomFont={handleRemoveFont}
            />
          </ThemeControl>
          <Divider />
          <ThemeControl token="font" label="Body font" {...impact}>
            <FontSelectorField
              {...methods}
              defaultValue={company_theme?.font}
              fieldName={'font'}
              options={fontOptions}
              customFonts={customFonts}
              onRemoveCustomFont={handleRemoveFont}
            />
          </ThemeControl>
        </Card>
        <UploadFontModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          companyId={companyId}
          // Select the font the moment it uploads. Uploading it and then making
          // the user find it in the list is a second step for something they
          // have already expressed the intent for.
          onUploaded={(fileUrl) => methods.setValue('font', fileUrl)}
        />
      </Grid>

      {/* BUTTONS */}
      <Grid item md={12}>
        <Card title="Buttons" spacing={6}>
          <ThemeControl token="border_radius" label="Border radius" {...impact}>
            <RadiusField
              {...methods}
              fieldName={'border_radius'}
              // `?.` on border_radius too, not just on company_theme. The chain stopped one link
              // short, so a theme that EXISTS but has no border_radius threw
              // "Cannot read properties of undefined (reading 'toString')" — and it threw
              // before the `|| '12'` fallback could apply, taking the whole Studio page down
              // with it. `??` rather than `||` so a legitimate 0 is not replaced by 12.
              defaultValue={company_theme?.border_radius?.toString() ?? '12'}
            />
          </ThemeControl>
        </Card>
      </Grid>

    </Stack>
  )
}
